import type { DecisionRow } from "@/api/client.js";
import { HelpTip } from "@/components/HelpTip.js";
import { Badge } from "@/components/ui/badge.js";
import { Button } from "@/components/ui/button.js";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card.js";
import { tooltips } from "@/copy/tooltips.js";
import { isValidIp, matchDecisions, type DecisionMatchScope } from "./match.js";
import { useIPFilter } from "./IPFilterContext.js";

type Props = {
  rows: DecisionRow[] | undefined;
  onBanThisIp?: (ip: string) => void;
  matchScope?: DecisionMatchScope;
};

export function IPInspectorCard({ rows, onBanThisIp, matchScope = "bans" }: Props) {
  const { query } = useIPFilter();
  const q = query.trim();
  if (!q || rows === undefined) return null;

  if (!isValidIp(q)) {
    return (
      <Card className="mb-4 border-destructive/50">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Invalid IP</CardTitle>
          <CardDescription>Enter a valid IPv4 or IPv6 address.</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  const { exact, partial } = matchDecisions(rows, q, { matchScope });
  const found = exact.length > 0;
  const foundLabel = matchScope === "all" ? "Found in current list" : "Found in current ban list";

  return (
    <Card className="mb-4" data-testid="ip-inspector">
      <CardHeader className="pb-2">
        <div className="flex flex-wrap items-center gap-2">
          <HelpTip content={tooltips.ipLookup}>
            <CardTitle className="text-base" tabIndex={0}>
              IP lookup
            </CardTitle>
          </HelpTip>
          <Badge variant={found ? "default" : "secondary"}>{found ? foundLabel : "Not found in current list"}</Badge>
          {onBanThisIp ? (
            <HelpTip content={tooltips.banThisIp}>
              <Button type="button" variant="destructive" size="sm" className="ml-auto" onClick={() => onBanThisIp(q)}>
                Ban this IP
              </Button>
            </HelpTip>
          ) : null}
        </div>
        <CardDescription>
          Based on bans <strong>loaded</strong> from <code className="rounded bg-muted px-1">/api/decisions</code> right now. This does not
          prove the IP is not blocked elsewhere (e.g. CAPI-only signals).
        </CardDescription>
      </CardHeader>
      {(exact.length > 0 || partial.length > 0) && (
        <CardContent className="space-y-2 text-sm">
          {exact.length > 0 ? (
            <div>
              <p className="font-medium text-foreground">Exact matches ({exact.length})</p>
              <ul className="list-inside list-disc text-muted-foreground">
                {exact.map((r) => (
                  <li key={r.id}>
                    {r.value} — id {r.id}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          {partial.length > 0 ? (
            <div>
              <p className="font-medium text-foreground">Partial matches ({partial.length})</p>
              <ul className="list-inside list-disc text-muted-foreground">
                {partial.map((r) => (
                  <li key={r.id}>
                    {r.value} — id {r.id}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </CardContent>
      )}
    </Card>
  );
}
